<template>
  <div class="printable-content simple-paper">
    <!-- header informations -->
    <div class="grid grid-cols-2">
      <!-- customer informations -->
      <div>
        <!-- proposal ID -->
        <div class="mb-7">
          <h2 class="text-slate-500 inline-block">#</h2>
          <h2 class="font-bold inline-block">{{ proposal.contractId }}</h2>
        </div>

        <!-- customer informations -->
        <div class="mb-5">
          <span class="block mb-1 font-bold">{{ customer.name }}</span>
          <span class="block mb-1">{{ customer.email }}</span>
          <span
            v-if="customer.phone"
            class="block mb-1"
          >{{ customer.phone }}</span>
        </div>
      </div>

      <!-- log and company informations -->
      <div>
        <!-- logo, address -->
        <div class="flex justify-end mb-7">
          <img
            :src="logo"
            alt="Uttrly"
          />
        </div>
        <div class="text-right">
          <span class="block mb-2">11810 Grand Park Ave, Suite 500</span>
          <span class="block mb-2">North Bethesda, MD 20852</span>
          <span class="block">202.681.0825</span>
        </div>
      </div>
    </div>

    <!-- proposal date and valid until -->
    <div class="flex gap-4">
      <span>
        <b>Proposal date:</b> {{ getLocalDate(proposal.contractDate) }}
      </span>
      <span>
        <b>Valid until:</b> {{ getLocalDate(proposal.contractValidDate) }}
      </span>
    </div>

    <!-- proposal items details -->
     <div class="pt-6 mt-6">
        <div>
          <h3 class="text-xl font-medium leading-10 tracking-tight">Details</h3>
        </div>

        <!-- original contract items -->
        <div class="mt-3">
          <div
            v-for="(lineItems, category) in getProposalItems"
            :key="category"
            class="mb-6"
          >
            <span class="font-semibold text-xl mb-2">{{ category }}</span>

            <div
              v-for="(lineItem) in lineItems"
              :key="lineItem.id"
              :class="[
                'border-b border-gray-200 py-4',
                lineItem.optional && !lineItem.selected ? 'opacity-50' : 'opacity-100'
              ]"
            >
              <div>
                <div class="grid grid-cols-5 gap-4">
                  <div class="col-span-4 flex items-start">
                    <div>
                      <div class="flex items-center">
                        <span class="font-bold">{{ lineItem.title }}</span>
                      </div>

                      <div class="text-justify" v-html="lineItem.scopeOfWork" />
                    </div>
                  </div>

                  <!-- line item total -->
                  <div class="flex flex-col gap-1 text-sm">
                    <p class="grid grid-cols-2">
                      <!-- <span class="font-semibold">Total Price:</span> -->
                      <span class="font-semibold">{{ currency(lineItem.totalPrice) }}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- category total -->
            <div class="mt-3">
              <div class="grid grid-cols-5 gap-4">
                <div class="col-span-4 flex items-start">
                  <div>
                    <div class="flex items-center">
                      <span class="font-bold">Total: </span>
                      <span>{{ currency(getCategoryTotalPrice(String(category))) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <!-- proposal total price -->
        <div class="mt-6">
          <div class="grid grid-cols-5 gap-4 border-t-2 border-gray-200 py-4">
            <div class="col-span-5 flex items-end">
              <div class="flex items-center gap-2">
                <span class="font-bold">Total:</span>
                <span>{{ currency(proposalTotalPrice) }}</span>
              </div>
            </div>
          </div>
        </div>

      </div>

    <!-- terms and conditions -->
    <div class="mt-6">
      <div class="text-justify" v-html="proposal.termsConditions" />
    </div>

  </div>
</template>

<script lang='ts' setup>
import { Budget } from '@/models/budget'
import { Customer } from '@/models/customer'
import { Project } from '@/models/project'
import { Proposal } from '@/models/proposal'
import { filter, contains } from 'underscore'
import logo from '@/assets/images/logo.png'
import { computed, PropType } from 'vue'
import { currency, sortLineItemsByCategoryName, toNumber } from '@/utils'

const props = defineProps({
  project: {
    type: Object as PropType<Project>,
    required: true
  },
  customer: {
    type: Object as PropType<Customer>,
    required: true
  },
  budget: {
    type: Object as PropType<Budget>,
    required: true
  },
  proposal: {
    type: Object as PropType<Proposal>,
    required: true
  },
})


const getLocalDate = computed(() => (date: Date) => {
  return new Date(date).toLocaleDateString()
})

const getProposalItems = computed(() => {
  const items = filter(props.budget.lineItems, (item) => contains(props.proposal.lineItems, item.id) && item.selected)
  const sortedItems = sortLineItemsByCategoryName(items)

  return sortedItems
})

const proposalTotalPrice = computed(() => {
  return props.budget.lineItems
    .filter(item => props.proposal.lineItems.includes(item.id) && item.selected)
    .reduce((total, item) => total + toNumber(item.totalPrice), 0)
})

const getCategoryTotalPrice = computed(() => (category: string) => {
  return props.budget.lineItems
    .filter(item => item.category.name === category && props.proposal.lineItems.includes(item.id) && item.selected)
    .reduce((total, item) => total + toNumber(item.totalPrice), 0)
})
</script>

<style lang="scss">
.printable-content {
  width: 100% !important; /* Largeur A4 */
  background: white !important;
}

.simple-paper {
  border: 0.7px solid #ccc;
}
</style>
